'use client';

import { useState } from 'react';
import { PortableText } from '@portabletext/react';
import Link from 'next/link';
import { buildProviderLink, isExternalLink } from '@/utils/blog/providerLinkHelpers';
import { htmlToPlainText } from '@/utils/sanitizeHtml';
import { t } from '@/i18n/translations';

/**
 * Парсит плейсхолдеры {Текст[CODE]} в Portable Text блоках
 * и преобразует их в реальные markDefs + marks для PortableText.
 *
 * Поддерживаемые типы:
 *   CITY_*    → cityLink    (ссылка на город)
 *   AFF_*     → link        (ссылка на сервис-страницу провайдера)
 *   AIRPORT_* → airportLink (ссылка на аэропорт)
 *   PLACE_*   → plain text  (достопримечательности — без ссылки)
 */
// FAQ страны строил ссылку {Yesim[AFF_YESIM]} как /{lang}/{страна}/yesim — такой страницы нет:
// 12 × 404 и 122 редиректа на 12 страницах /cities (краул 29.09.2026). Провайдер живёт под своей
// вертикалью; неизвестный код — текст без ссылки.
const PROVIDER_VERTICAL = {
    airalo: 'esim', yesim: 'esim', saily: 'esim', ubigi: 'esim', voye: 'esim', iroamly: 'esim',
    esim4travel: 'esim', amigoesim: 'esim',
    nordvpn: 'vpn', surfshark: 'vpn', protonvpn: 'vpn',
    localrent: 'transport', kiwitaxi: 'transport', welcomepickups: 'transport', discovercars: 'transport',
    economybookings: 'transport', bikesbooking: 'transport', omio: 'transport', aviasales: 'transport',
    viator: 'tours', tripster: 'tours',
    booking: 'stay', trip: 'stay',
    ekta: 'insurance', cherehapa: 'insurance',
};
// Коды городов в текстах FAQ, не совпадающие со слагом в Sanity.
const CITY_CODE_ALIASES = { SHKODRA: 'shkoder', STEPANTSMINDA_KAZBEGI: 'kazbegi', STEPANTSMINDA: 'kazbegi' };

function resolveFaqPlaceholders(blocks, lang, cSlug) {
    if (!blocks || !Array.isArray(blocks)) return blocks;

    const PLACEHOLDER_RE = /\{([^[]+)\[([A-Za-z][A-Za-z0-9_-]*)\]\}/g;

    return blocks.map(block => {
        if (block._type !== 'block' || !block.children?.length) return block;

        // Quick check: does any span contain a placeholder?
        const hasAny = block.children.some(
            c => c._type === 'span' && PLACEHOLDER_RE.test(c.text)
        );
        if (!hasAny) return block;

        const newChildren = [];
        const newMarkDefs = [...(block.markDefs || [])];
        let counter = 0;

        for (const child of block.children) {
            if (child._type !== 'span') {
                newChildren.push(child);
                continue;
            }

            const text = child.text;
            PLACEHOLDER_RE.lastIndex = 0;
            let lastIdx = 0;
            let match;
            let found = false;

            while ((match = PLACEHOLDER_RE.exec(text)) !== null) {
                found = true;
                const [full, displayText, rawCode] = match;
                // Нормализуем код: uppercase + подчёркивания
                const code = rawCode.toUpperCase().replace(/-/g, '_');

                // Text before the placeholder
                if (match.index > lastIdx) {
                    newChildren.push({
                        ...child,
                        _key: `${child._key}_t${counter}`,
                        text: text.slice(lastIdx, match.index),
                    });
                }

                const markKey = `ph_${counter++}`;

                const providerCode = code.startsWith('AFF_') ? code.slice(4).toLowerCase() : null;
                const providerVertical = providerCode ? PROVIDER_VERTICAL[providerCode] : null;

                if (code.startsWith('CITY_')) {
                    const cityCode = code.slice(5);
                    const citySlug = CITY_CODE_ALIASES[cityCode] || cityCode.toLowerCase().replace(/_/g, '-');
                    newMarkDefs.push({
                        _key: markKey,
                        _type: 'cityLink',
                        reference: { slug: { current: citySlug }, countrySlug: cSlug },
                    });
                    newChildren.push({
                        ...child,
                        _key: `${child._key}_l${counter}`,
                        text: displayText,
                        marks: [...(child.marks || []), markKey],
                    });
                } else if (providerVertical) {
                    newMarkDefs.push({
                        _key: markKey,
                        _type: 'link',
                        href: `/${lang}/${cSlug}/${providerVertical}/${providerCode}`,
                    });
                    newChildren.push({
                        ...child,
                        _key: `${child._key}_l${counter}`,
                        text: displayText,
                        marks: [...(child.marks || []), markKey],
                    });
                } else if (code.startsWith('AIRPORT_')) {
                    const iata = code.slice(8);
                    newMarkDefs.push({
                        _key: markKey,
                        _type: 'airportLink',
                        reference: { iata, countrySlug: cSlug },
                    });
                    newChildren.push({
                        ...child,
                        _key: `${child._key}_l${counter}`,
                        text: displayText,
                        marks: [...(child.marks || []), markKey],
                    });
                } else {
                    // PLACE_* and unknown codes → plain text, no link
                    newChildren.push({
                        ...child,
                        _key: `${child._key}_p${counter}`,
                        text: displayText,
                    });
                }

                lastIdx = match.index + full.length;
            }

            // Remaining text after last placeholder
            if (found && lastIdx < text.length) {
                newChildren.push({
                    ...child,
                    _key: `${child._key}_e${counter}`,
                    text: text.slice(lastIdx),
                });
            }

            // No placeholders in this span — keep as-is
            if (!found) {
                newChildren.push(child);
            }
        }

        return { ...block, children: newChildren, markDefs: newMarkDefs };
    });
}

/**
 * Country FAQ - журнальный стиль
 * Аккордеон с вопросами и ответами
 * accentColor - цвет акцента (по умолчанию золотой #7d6637, для сервисов свой)
 */
export default function CountryFAQ({ faqItems, lang, countryName = '', countrySlug = null, compact = false, accentColor = '#7d6637', variant = 'default' }) {
    const [openIndex, setOpenIndex] = useState(0); // Первый вопрос открыт по умолчанию
    const ui = t(lang, 'countryWidgetsUi');

    if (!faqItems || faqItems.length === 0) return null;

    // Конфигурация PortableText для рендеринга ссылок
    const portableTextComponents = {
        marks: {
            // Стандартная ссылка (с href)
            link: ({ children, value }) => {
                // Ссылки на свой домен в FAQ аэропортов записаны абсолютными, с IATA в верхнем
                // регистре: открывались в новой вкладке с nofollow и шли через 301 (обход 01.10.2026).
                let href = value?.href || '#';
                const own = href.match(/^https?:\/\/(?:www\.)?triplinkhub\.com(\/[^\s]*)?$/i);
                if (own) href = (own[1] || '/').replace(/\/airport\/([A-Za-z]{3})(?=[/?#]|$)/, (m, code) => `/airport/${code.toLowerCase()}`);
                const isExternal = /^https?:\/\//i.test(href);
                return (
                    <a
                        href={href}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "nofollow noopener noreferrer" : undefined}
                        className="text-blue-600 hover:text-blue-800 underline"
                    >
                        {children}
                    </a>
                );
            },
            // Ссылка на провайдера
            providerLink: ({ children, value }) => {
                if (!value?.reference?.code) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ FAQ providerLink: no reference.code', JSON.stringify(value));
                    }
                    return <span>{children}</span>;
                }
                const provider = value.reference;
                const href = buildProviderLink(provider, lang, countrySlug);
                const isExternal = isExternalLink(href);
                return (
                    <a
                        href={href}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "nofollow noopener noreferrer sponsored" : undefined}
                        className="text-blue-600 hover:text-blue-800 underline"
                    >
                        {children}
                    </a>
                );
            },
            // Ссылка на город
            cityLink: ({ children, value }) => {
                if (!value?.reference?.slug?.current && !value?.reference?.slug) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ FAQ cityLink: no slug in reference', JSON.stringify(value));
                    }
                    return <span>{children}</span>;
                }
                const citySlug = value.reference.slug?.current || value.reference.slug;
                const cityCountrySlug = value.reference.countrySlug || value.reference.country?.slug?.current;

                // Проверка на валидность слагов
                if (!citySlug || citySlug === 'null' || !cityCountrySlug || cityCountrySlug === 'null') {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ FAQ cityLink: invalid slugs', { citySlug, cityCountrySlug });
                    }
                    return <span>{children}</span>;
                }
                
                const href = `/${lang}/${cityCountrySlug}/${citySlug}`;
                return (
                    <Link href={href} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            // Ссылка на POI (достопримечательность)
            poiLink: ({ children, value }) => {
                if (!value?.reference?.slug?.current && !value?.reference?.slug) {
                    return <span>{children}</span>;
                }
                const poiSlug = value.reference.slug?.current || value.reference.slug;
                const poiCitySlug = value.reference.citySlug || value.reference.city?.slug?.current;
                const poiCountrySlug = value.reference.countrySlug || value.reference.country?.slug?.current;

                // Проверка на валидность слагов
                if (!poiSlug || poiSlug === 'null' || !poiCitySlug || poiCitySlug === 'null' || !poiCountrySlug || poiCountrySlug === 'null') {
                    return <span>{children}</span>;
                }
                
                const href = `/${lang}/${poiCountrySlug}/${poiCitySlug}/places/${poiSlug}`;
                return (
                    <Link href={href} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            // Ссылка на сервис
            serviceLink: ({ children, value }) => {
                if (!value?.reference?.code) {
                    return <span>{children}</span>;
                }
                const serviceCode = value.reference.code;
                const slug = countrySlug || (countryName ? countryName.toLowerCase().replace(/\s+/g, '-') : '');
                if (!slug) {
                    return <span>{children}</span>;
                }
                const href = `/${lang}/${slug}/${serviceCode}`;
                return (
                    <Link href={href} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            // Ссылка на статью блога
            internalLink: ({ children, value }) => {
                if (!value?.reference?.slug?.current && !value?.reference?.slug) {
                    return <span>{children}</span>;
                }
                const postSlug = value.reference.slug?.current || value.reference.slug;
                const href = `/${lang}/blog/${postSlug}`;
                return (
                    <Link href={href} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            // Ссылка на аэропорт
            airportLink: ({ children, value }) => {
                if (!value?.reference?.iata) {
                    return <span>{children}</span>;
                }
                const iata = value.reference.iata;
                const countrySlug = value.reference.countrySlug;
                if (!countrySlug) {
                    return <span>{children}</span>;
                }
                const href = `/${lang}/${countrySlug}/airport/${String(iata).toLowerCase()}`;
                return (
                    <Link href={href} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
        },
        // Отладка: неизвестные типы блоков
        unknownMark: ({ children, markType, value }) => {
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ FAQ unknown mark type:', markType, JSON.stringify(value));
            }
            return <span>{children}</span>;
        },
    };

    const renderLegacyHtml = (html) => htmlToPlainText(html)
        .split('\n')
        .map((line, index) => (
            <span key={`${index}-${line.slice(0, 20)}`}>
                {index > 0 && <br />}
                {line}
            </span>
        ));

    // Shared answer renderer (string / legacy HTML as inert text / Portable Text)
    const renderAnswer = (item) => (
        typeof item.answer === 'string' ? (
            item.answer.includes('<a ') || item.answer.includes('<br') || item.answer.includes('<button') ? (
                <div>{renderLegacyHtml(item.answer)}</div>
            ) : (
                <p>{item.answer}</p>
            )
        ) : (
            <PortableText
                value={resolveFaqPlaceholders(item.answer, lang, countrySlug)}
                components={portableTextComponents}
            />
        )
    );

    // "Gallery · Field" variant — native <details> ledger accordion.
    // The page supplies its own gf-mark/h2 header, so this renders the list only.
    if (variant === 'gf') {
        return (
            <div className="gf-faq" aria-label={ui.faq.aria(countryName)}>
                {faqItems.map((item, index) => (
                    <details className="fq" key={index} open={index === 0}>
                        <summary>{item.question}<span className="pl">+</span></summary>
                        <div className="ans">{renderAnswer(item)}</div>
                    </details>
                ))}
            </div>
        );
    }

    const inner = (
        <>
            {/* Header */}
            <div className="mb-10">
                <span className="text-[11px] font-medium uppercase tracking-[0.15em] mb-2 block" style={{ color: accentColor }}>
                    {ui.faq.eyebrow}
                </span>
                <h2 className={`article-display font-bold text-gray-900 leading-snug ${compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'}`}>
                    {ui.faq.heading}
                </h2>
                <div className="mt-4 w-16 h-[2px]" style={{ backgroundColor: accentColor }} />
            </div>

            {/* Accordion */}
            <div>
                {faqItems.map((item, index) => {
                    const isOpen = openIndex === index;

                    return (
                        <div
                            key={index}
                            className="border-b"
                            style={{ borderColor: '#dce3df' }}
                        >
                            <button
                                onClick={() => setOpenIndex(isOpen ? -1 : index)}
                                className="w-full flex items-center gap-4 py-5 text-left group"
                                aria-expanded={isOpen}
                                aria-controls={`faq-answer-${index}`}
                            >
                                <span
                                    className={`flex-1 article-display font-semibold leading-snug text-gray-900 group-hover:opacity-70 transition-opacity duration-200 ${compact ? 'text-sm sm:text-base' : 'text-base sm:text-[17px]'}`}
                                >
                                    {item.question}
                                </span>

                                <span className={`flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
                                    <svg className="w-5 h-5" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                                    </svg>
                                </span>
                            </button>

                            <div
                                id={`faq-answer-${index}`}
                                className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
                                role="region"
                                aria-labelledby={`faq-question-${index}`}
                            >
                                <div className="pb-6">
                                    <div
                                        className="text-gray-600 text-[15px] leading-[1.8] prose prose-sm max-w-none prose-p:my-2 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                                    >
                                        {typeof item.answer === 'string' ? (
                                            item.answer.includes('<a ') || item.answer.includes('<br') || item.answer.includes('<button') ? (
                                                <div>{renderLegacyHtml(item.answer)}</div>
                                            ) : (
                                                <p>{item.answer}</p>
                                            )
                                        ) : (
                                            <PortableText
                                                value={resolveFaqPlaceholders(item.answer, lang, countrySlug)}
                                                components={portableTextComponents}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );

    if (compact) {
        return (
            <div
                aria-label={ui.faq.aria(countryName)}
            >
                {inner}
            </div>
        );
    }

    return (
        <section
            className="border-t py-14 sm:py-20"
            style={{ borderColor: '#dce3df' }}
            aria-label={ui.faq.aria(countryName)}
        >
            <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10">
                {inner}
            </div>
        </section>
    );
}
